import React, { useState, useEffect, useRef } from 'react'

// PAT-style knee ROM figure: outlined-pill thigh + shin + shoe.
// The shin and shoe rotate as one group around the knee, so the angle
// renders smoothly across the full range — no bucketed images.
//
// Coordinate frame (viewBox 0 0 200 210):
//   HIP at (58,108), KNEE at (95,108)  → horizontal thigh
//   SHIN length 70, rotated about KNEE by displayAngle
//   At 0°  the leg is fully extended (shin points right).
//   At 90° the shin hangs straight down (seated, knee at 90°).
// Default ROM zone ranges. Hard-coded today; the long-term plan is to load these
// per-patient from the server (clinical goal set by physio, then sent down as
// part of the assignment / care plan). Each value is [startAngleDeg, endAngleDeg]
// in our display coordinate system (0° = full extension, 90° = seated knee bend).
export const DEFAULT_ROM_BANDS = {
  green:  [-10, 155],   // full valid range
  yellow: [15, 75],     // acceptable target
  blue:   [40, 50],     // ideal target
}

export default function KneeROMFigure({
  extension, flexion, liveAngle,
  maxWidth = 260,
  bandRanges = null,    // { green:[a1,a2], yellow:[a1,a2], blue:[a1,a2] } | null
  showRangeFan = false, // orange achieved-range fan at radius LEG (legacy)
  showTrackingPath = false, // achieved-range sector at half the band radius
  noClamp = false,      // true → no [-15, 175] clamp; useful for goniometer debug
  // Sit-to-Stand visualization (mirrors physio_app_web's KneeROMFigure):
  // as displayAngle drops from 90° toward 0°, the figure rises out of
  // the chair — knee anchored, hip + torso + head + arm rotate up
  // around the knee, shin stays vertical down to the planted foot.
  standUpMode = false,
  // Live-angle red chevron at the outer band edge, pointing inward at
  // the live angle. Opt-in so legacy callers keep their unchanged
  // figure; the admin preview turns it on so the clinician sees the
  // same indicator the patient sees in-session.
  showTickIndicator = false,
}) {
  const [displayAngle, setDisplayAngle] = useState(90)
  const animRef = useRef(null)
  const hasLive    = liveAngle !== null && liveAngle !== undefined
  const hasHistory = extension !== null && extension !== undefined

  // In normal patient-app use, clamp to a sensible knee-flexion range so a
  // glitched read can't spin the figure into impossible positions. Debug
  // pages pass `noClamp` to disable this and surface raw device behaviour.
  const clampLive = (v) => noClamp ? v : Math.max(-15, Math.min(175, v))
  const clampHist = (v) => noClamp ? v : Math.max(-15, Math.min(180, v))

  useEffect(() => {
    if (hasLive) {
      clearInterval(animRef.current)
      setDisplayAngle(clampLive(liveAngle))
      return
    }
    clearInterval(animRef.current)
    if (!hasHistory) { setDisplayAngle(90); return }
    const ext  = clampHist(extension)
    const flex = clampHist(flexion)
    if (Math.abs(flex - ext) < 2) { setDisplayAngle(flex); return }
    let t = 0
    const STEP = 16, CYCLE = 2000
    animRef.current = setInterval(() => {
      t = (t + STEP) % (CYCLE * 2)
      const p = t < CYCLE ? t / CYCLE : 2 - t / CYCLE
      setDisplayAngle(ext + p * (flex - ext))
    }, STEP)
    return () => clearInterval(animRef.current)
  }, [liveAngle, extension, flexion])

  const toRad = deg => (deg * Math.PI) / 180
  const KNEE_X = 95, KNEE_Y = 108
  const HIP_X  = 58, HIP_Y  = 108
  const LEG = 70
  // Outer radius of the coloured bands (green/yellow/blue sectors).
  // Hoisted so the live-angle tick indicator can sit just outside it.
  const BAND_R = 82
  const THIGH_LEN   = KNEE_X - HIP_X
  const THIGH_THICK = 18
  const SHIN_THICK  = 16
  const TORSO_THICK = 18
  const ARM_THICK   = 12
  const HEAD_R      = 12

  // Stand-up dynamic geometry.
  //   t = 0 at displayAngle 90° (fully seated) → upper body stays where
  //       the original drawing puts it.
  //   t = 1 at displayAngle 0° (fully standing) → thigh has rotated 90°
  //       so it points straight up; hip sits directly above the knee.
  // The knee position is fixed at (KNEE_X, KNEE_Y) and the shin always
  // points straight down to the planted foot, regardless of t.
  const standUpT = standUpMode
    ? Math.max(0, Math.min(1, (90 - displayAngle) / 90))
    : 0
  const thighWorldDeg = 180 + standUpT * 90
  const thighRad = (thighWorldDeg * Math.PI) / 180
  const dynHipX = KNEE_X + THIGH_LEN * Math.cos(thighRad)
  const dynHipY = KNEE_Y + THIGH_LEN * Math.sin(thighRad)
  const HIP_X_DRAW = standUpMode ? dynHipX : HIP_X
  const HIP_Y_DRAW = standUpMode ? dynHipY : HIP_Y

  // Torso: vertical pill from upper chest down to hip
  const torsoTop = HIP_Y_DRAW - 56
  const torsoLen = (HIP_Y_DRAW - 4) - torsoTop

  // Arm: diagonal pill from upper torso forward toward the seat
  const armX1 = HIP_X_DRAW + 2,  armY1 = HIP_Y_DRAW - 32
  const armX2 = HIP_X_DRAW + 22, armY2 = HIP_Y_DRAW - 7
  const armDx = armX2 - armX1, armDy = armY2 - armY1
  const armLen = Math.sqrt(armDx * armDx + armDy * armDy)
  const armDeg = Math.atan2(armDy, armDx) * 180 / Math.PI

  const extAngle  = hasHistory ? clampHist(extension) : null
  const flexAngle = hasHistory ? clampHist(flexion)   : null
  const curRad   = toRad(displayAngle)
  const curFootX = KNEE_X + LEG * Math.cos(curRad)
  const curFootY = KNEE_Y + LEG * Math.sin(curRad)

  let fanPath = null
  if (hasHistory && extAngle !== null && flexAngle !== null) {
    const extR = toRad(extAngle), flexR = toRad(flexAngle)
    const ex = (KNEE_X + LEG * Math.cos(extR)).toFixed(1)
    const ey = (KNEE_Y + LEG * Math.sin(extR)).toFixed(1)
    const fx = (KNEE_X + LEG * Math.cos(flexR)).toFixed(1)
    const fy = (KNEE_Y + LEG * Math.sin(flexR)).toFixed(1)
    const largeArc = (flexAngle - extAngle) > 180 ? 1 : 0
    fanPath = `M ${KNEE_X} ${KNEE_Y} L ${ex} ${ey} A ${LEG} ${LEG} 0 ${largeArc} 1 ${fx} ${fy} Z`
  }

  // Tracking path — same shape as fanPath but at half the band radius (so it
  // sits inside the leg, visually distinct from the green/yellow/blue zones).
  // Updates live as the patient achieves new extremes during a ROM Test.
  let trackingPath = null
  if (showTrackingPath && hasHistory && extAngle !== null && flexAngle !== null && extAngle < flexAngle) {
    const TRACK_R = 41 // half of BAND_R (82)
    const extR = toRad(extAngle), flexR = toRad(flexAngle)
    const ex = (KNEE_X + TRACK_R * Math.cos(extR)).toFixed(1)
    const ey = (KNEE_Y + TRACK_R * Math.sin(extR)).toFixed(1)
    const fx = (KNEE_X + TRACK_R * Math.cos(flexR)).toFixed(1)
    const fy = (KNEE_Y + TRACK_R * Math.sin(flexR)).toFixed(1)
    const largeArc = (flexAngle - extAngle) > 180 ? 1 : 0
    trackingPath = `M ${KNEE_X} ${KNEE_Y} L ${ex} ${ey} A ${TRACK_R} ${TRACK_R} 0 ${largeArc} 1 ${fx} ${fy} Z`
  }

  const OUTLINE = '#bdbdbd'
  const FILL    = '#ffffff'
  const LIVE    = '#ff9800'
  const STROKE_W = 2.5
  const shinStroke = hasLive ? LIVE : OUTLINE

  // Label sits at the shin midpoint, offset perpendicular to the shin so it
  // always clears the limb regardless of angle (perp_right = rotate shin dir CW).
  const LABEL_PERP = 22
  const midX = (KNEE_X + curFootX) / 2
  const midY = (KNEE_Y + curFootY) / 2
  const labelX = (midX + LABEL_PERP * Math.sin(curRad)).toFixed(1)
  const labelY = (midY - LABEL_PERP * Math.cos(curRad)).toFixed(1)

  return (
    <svg
      viewBox={standUpMode ? '0 -40 200 250' : '0 0 200 210'}
      width="100%"
      style={{ maxWidth, display: 'block', margin: '0 auto' }}
    >
      {/* Card background — extends upward in stand-up mode to give the
          standing head + torso room. */}
      <rect
        x={0}
        y={standUpMode ? -40 : 0}
        width="200"
        height={standUpMode ? 250 : 210}
        rx="16"
        fill="#fafafa"
        stroke="#e0e0e0"
        strokeWidth="1"
      />

      {/* Seat hint (subtle П shape under the thigh, like the PAT seat) */}
      <path
        d={`M 30 ${HIP_Y + 9} L 30 ${HIP_Y + 29} M 30 ${HIP_Y + 9} L ${KNEE_X} ${HIP_Y + 9} M ${KNEE_X} ${HIP_Y + 9} L ${KNEE_X} ${HIP_Y + 29}`}
        stroke="#dcdcdc" strokeWidth="6" strokeLinecap="round" fill="none"
      />

      {/* ROM zone bands — filled sectors at the same outer radius, layered so
          the smaller (more specific) zones cover the larger ones. The leg renders
          on top so it always sits above the bands. */}
      {bandRanges && (() => {
        const sector = (a1, a2) => {
          const r1 = toRad(a1), r2 = toRad(a2)
          const sx = (KNEE_X + BAND_R * Math.cos(r1)).toFixed(1)
          const sy = (KNEE_Y + BAND_R * Math.sin(r1)).toFixed(1)
          const ex = (KNEE_X + BAND_R * Math.cos(r2)).toFixed(1)
          const ey = (KNEE_Y + BAND_R * Math.sin(r2)).toFixed(1)
          const large = (a2 - a1) > 180 ? 1 : 0
          return `M ${KNEE_X} ${KNEE_Y} L ${sx} ${sy} A ${BAND_R} ${BAND_R} 0 ${large} 1 ${ex} ${ey} Z`
        }
        return (
          <g>
            {bandRanges.green  && <path d={sector(...bandRanges.green)}  fill="#669d34" opacity="0.85"/>}
            {bandRanges.yellow && <path d={sector(...bandRanges.yellow)} fill="#fefb41" opacity="0.9"/>}
            {bandRanges.blue   && <path d={sector(...bandRanges.blue)}   fill="#bdb6b6" opacity="0.95"/>}
          </g>
        )
      })()}

      {/* Orange achieved-range fan — hidden by default; flag re-enables when needed */}
      {showRangeFan && fanPath && <path d={fanPath} fill="rgba(255,152,0,0.35)" stroke="none"/>}

      {/* Tracking path — sector showing the session's extension→flexion at
          half the band radius. Renders on top of the bands but below the
          leg so the live shin still draws over it. */}
      {trackingPath && <path d={trackingPath} fill="rgba(255,152,0,0.55)" stroke="rgba(245,124,0,0.9)" strokeWidth="1"/>}

      {/* Torso pill (drawn first so the thigh joint sits in front) */}
      <rect
        x={HIP_X_DRAW - TORSO_THICK / 2}
        y={torsoTop - TORSO_THICK / 2}
        width={TORSO_THICK}
        height={torsoLen + TORSO_THICK}
        rx={TORSO_THICK / 2}
        fill={FILL} stroke={OUTLINE} strokeWidth={STROKE_W}
      />

      {/* Thigh — rotates around the knee toward the hip. In stand-up
          mode the thigh swings from horizontal (seated) to vertical
          (standing) as the patient rises; in default mode it stays
          horizontal. */}
      <g transform={`translate(${KNEE_X} ${KNEE_Y}) rotate(${thighWorldDeg.toFixed(2)})`}>
        <rect
          x={-THIGH_THICK / 2}
          y={-THIGH_THICK / 2}
          width={THIGH_LEN + THIGH_THICK}
          height={THIGH_THICK}
          rx={THIGH_THICK / 2}
          fill={FILL} stroke={OUTLINE} strokeWidth={STROKE_W}
        />
      </g>

      {/* Arm pill (diagonal, drawn over torso/thigh joint) */}
      <g transform={`translate(${armX1} ${armY1}) rotate(${armDeg.toFixed(2)})`}>
        <rect
          x={-ARM_THICK / 2}
          y={-ARM_THICK / 2}
          width={armLen + ARM_THICK}
          height={ARM_THICK}
          rx={ARM_THICK / 2}
          fill={FILL} stroke={OUTLINE} strokeWidth={STROKE_W}
        />
      </g>

      {/* Head (drawn last among the upper-body parts so it's clean) */}
      <circle
        cx={HIP_X_DRAW} cy={HIP_Y_DRAW - 70} r={HEAD_R}
        fill={FILL} stroke={OUTLINE} strokeWidth={STROKE_W}
      />

      {/* Shin — in default mode rotates by displayAngle. In stand-up
          mode the shin stays vertical (90°) because the foot is planted
          on the floor below the knee. */}
      <g
        transform={`translate(${KNEE_X} ${KNEE_Y}) rotate(${(standUpMode ? 90 : displayAngle).toFixed(2)})`}
      >
        <rect
          x={-SHIN_THICK / 2}
          y={-SHIN_THICK / 2}
          width={LEG + SHIN_THICK}
          height={SHIN_THICK}
          rx={SHIN_THICK / 2}
          fill={FILL} stroke={shinStroke} strokeWidth={STROKE_W}
        />
      </g>

      {/* Angle label — centered perpendicular to the shin midpoint */}
      {(hasLive || hasHistory) ? (
        <text x={labelX} y={labelY} fontSize="13" fill="#333" fontWeight="bold"
          textAnchor="middle" dominantBaseline="middle">
          {displayAngle.toFixed(0)}°
        </text>
      ) : (
        <text x={labelX} y={labelY} fontSize="11" fill="#bdbdbd"
          textAnchor="middle" dominantBaseline="middle">
          {displayAngle.toFixed(0)}°
        </text>
      )}

      {/* Live-angle red chevron at the outer band edge, pointing inward
          toward the knee. Tracks displayAngle so it follows the live
          position as the preview slider moves. */}
      {showTickIndicator && (hasLive || hasHistory) && (
        <g transform={`translate(${KNEE_X} ${KNEE_Y}) rotate(${displayAngle.toFixed(2)})`}>
          <polygon
            points={`${BAND_R + 3},0 ${BAND_R + 12},-6 ${BAND_R + 12},6`}
            fill="#e53935"
            stroke="#b71c1c"
            strokeWidth="0.75"
            strokeLinejoin="round"
          />
        </g>
      )}

      {/* Bottom status line */}
      {hasLive && (
        <text x="100" y="202" fontSize="9" fill="#9e9e9e" textAnchor="middle">LIVE ROM TEST</text>
      )}
      {hasHistory && !hasLive && extAngle !== null && (
        <text x="100" y="202" fontSize="9" fill="#9e9e9e" textAnchor="middle">
          ext {extAngle.toFixed(0)}°  flex {flexAngle.toFixed(0)}°  range {Math.abs(flexAngle - extAngle).toFixed(0)}°
        </text>
      )}
    </svg>
  )
}
